import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { AdressesService, Adresse, AdressePayload, AdresseType } from '../../../../services/adresses.service';
import { ClientsService, Client } from '../../../../services/clients.service';
import {ChangeDetectorRef} from '@angular/core';
@Component({
  selector: 'app-adresses-create-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './adresses.component.html',
  styleUrl: './adresses.component.scss'
})
export class AdressesCreateEditComponent implements OnInit {
  adresses: Adresse[] = [];
  clients: Client[] = [];
  selectedId = '';
  isEditMode = false;
  form: AdressePayload = {
    nom: '',
    prenom: '',
    service: '',
    raison_sociale: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'France',
    type_adresse: 'livraison',
    client_id: 0
  };
  error = '';
  success = '';
  loading = false;
  loadingList = false;
  deletingId: number | null = null;

  constructor(
    private readonly adressesService: AdressesService,
    private readonly clientsService: ClientsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  get pageTitle(): string {
    return this.isEditMode ? 'Modifier une adresse' : 'Créer une adresse';
  }

  private getClientIdentifiant(clientId: number): string {
    return this.clients.find((client) => client.id === clientId)?.identifiant ?? `Client #${clientId}`;
  }

  getAdresseOptionLabel(adresse: Adresse): string {
    const client = this.getClientIdentifiant(adresse.client_id);
    const ligneAdresse = (adresse.adresse || adresse.nom || '-').trim() || '-';
    const ville = (adresse.ville || '-').trim() || '-';
    return `${client} - ${ligneAdresse} - ${ville}`;
  }

  onSelectedAdresseChange(value: string): void {
    this.selectedId = value;
    if (!value) {
      this.clearForm();
      return;
    }
    this.selectAdresse(Number(value));
    this.cdr.detectChanges();
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const response = err as { error?: unknown; message?: unknown } | null | undefined;

    const fromValue = (value: unknown): string | null => {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
      if (Array.isArray(value)) {
        const items = value
          .map((item) => fromValue(item))
          .filter((item): item is string => Boolean(item));
        return items.length ? items.join(' ') : null;
      }
      if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        return (
          fromValue(record['detail']) ||
          fromValue(record['message']) ||
          fromValue(record['error']) ||
          fromValue(record['msg']) ||
          fromValue(record['reason']) ||
          null
        );
      }
      return null;
    };

    return (
      fromValue(response?.error) ||
      fromValue(response?.message) ||
      fallback
    );
  }

  ngOnInit(): void {
    this.isEditMode = this.router.url.includes('/gestion-logistiques/adresses/modifier');
    this.loadAdresses();
    this.clientsService.list().subscribe((clients) => {
      this.clients = clients;
      this.cdr.detectChanges();
    });
    // Si on a un paramètre id dans l'URL, charger l'adresse pour édition
    this.route.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.adressesService.get(Number(id)).subscribe({
          next: (adresse) => {
            this.form = { ...adresse };
            this.selectedId = String(adresse.id);
            this.cdr.detectChanges();
          },
          error: () => {
            this.error = "Impossible de charger l'adresse à éditer.";
            this.selectedId = '';
            this.cdr.detectChanges();
          }
        });
      } else {
        this.selectedId = '';
      }
    });
  }

  loadAdresses(): void {
    this.loadingList = true;
    this.adressesService.list().subscribe({
      next: (adresses) => {
        this.adresses = adresses;
        this.loadingList = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Erreur lors du chargement des adresses';
        this.loadingList = false;
      }
    });
  }

  selectAdresse(id: number): void {
    const adresse = this.adresses.find(a => a.id === id);
    if (adresse) {
      this.selectedId = String(adresse.id);
      this.form = { ...adresse };
    }
  }

  clearForm(): void {
    this.selectedId = '';
    this.form = {
      nom: '',
      prenom: '',
      service: '',
      raison_sociale: '',
      email: '',
      telephone: '',
      adresse: '',
      code_postal: '',
      ville: '',
      pays: 'France',
      type_adresse: 'livraison',
      client_id: 0
    };
  }

  createOrUpdate(): void {
    this.error = '';
    this.success = '';
    this.loading = true;

    if (this.isEditMode && !this.selectedId) {
      this.error = 'Veuillez sélectionner une adresse à modifier.';
      this.loading = false;
      return;
    }

    if (!this.form.client_id || !this.form.adresse?.trim() || !this.form.code_postal?.trim() || !this.form.ville?.trim() || !this.form.pays?.trim()) {
      this.error = 'Le client, l\'adresse, le code postal, la ville et le pays sont obligatoires.';
      this.loading = false;
      return;
    }
    if (this.selectedId) {
      this.adressesService.update(Number(this.selectedId), this.form).subscribe({
        next: (updatedAdresse) => {
          this.success = 'Adresse modifiée.';
          this.selectedId = String(updatedAdresse.id);
          this.form = { ...updatedAdresse };
          this.loadAdresses();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.error = this.extractErrorMessage(err, 'Erreur lors de la modification.');
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    } else {
      this.adressesService.create(this.form).subscribe({
        next: () => {
          this.success = 'Adresse créée.';
          this.clearForm();
          this.loadAdresses();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.error = this.extractErrorMessage(err, 'Erreur lors de la création.');
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  deleteAdresse(id: number): void {
    if (!confirm('Supprimer cette adresse ?')) return;
    this.deletingId = id;
    this.adressesService.delete(id).subscribe({
      next: () => {
        this.success = 'Adresse supprimée.';
        this.loadAdresses();
        this.deletingId = null;
      },
      error: () => {
        this.error = 'Erreur lors de la suppression.';
        this.deletingId = null;
      }
    });
  }

  
}
